import React, { useState, useRef } from 'react';
import { PlusCircle } from 'lucide-react';
import { supabase } from '../../services/supabase';
import { DOC_TYPES, TABLE_NAMES } from '../../constants';
import JournalEntryMetaFields from './JournalEntryMetaFields';
import JournalContentEditor from './JournalContentEditor';

const AddPostForm = ({ isAdmin, authorId, setMessage, onPostAdded }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  const [imageUrls, setImageUrls] = useState(['']);
  const [docType, setDocType] = useState(DOC_TYPES.POST);
  const [changelog, setChangelog] = useState([]);
  const contentEditorRef = useRef(null);

  if (!isAdmin) return null;

  const handleAddImageField = () => {
    setImageUrls([...imageUrls, '']);
  };

  const handleRemoveImageField = (index) => {
    setImageUrls(imageUrls.filter((_, i) => i !== index));
  };

  const handleImageUrlChange = (index, value) => {
    const newUrls = [...imageUrls];
    newUrls[index] = value;
    setImageUrls(newUrls);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = contentEditorRef.current?.flush?.() ?? content;
    if (!title || !body) return;

    try {
      const validImageUrls = imageUrls
        .map(url => url.trim())
        .filter(url => url.length > 0);

      const now = new Date().toISOString();
      const cleanedChangelog = changelog
        .filter((entry) => entry.date || entry.note?.trim())
        .map((entry) => ({ date: entry.date, note: entry.note.trim() }));

      const { error } = await supabase
        .from(TABLE_NAMES.BLOG_POSTS)
        .insert({
          title,
          content: body,
          summary: body.substring(0, 150) + '...',
          date: now,
          updated_at: now,
          tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
          author_id: authorId,
          image_urls: validImageUrls.length > 0 ? validImageUrls : null,
          created_at: now,
          doc_type: docType,
          changelog: docType === DOC_TYPES.LIVING ? cleanedChangelog : [],
        });

      if (error) throw error;
      setMessage("New journal entry published!");
      setTitle('');
      setContent('');
      setTags('');
      setImageUrls(['']);
      setDocType(DOC_TYPES.POST);
      setChangelog([]);
      if (onPostAdded) onPostAdded();
    } catch (error) {
      console.error("Error adding entry: ", error);
      setMessage("Failed to publish entry.");
    }
    setTimeout(() => setMessage(''), 3000);
  };

  return (
    <div className="p-6 bg-yellow-50 rounded-xl shadow-lg border border-yellow-200 space-y-4">
      <h3 className="text-xl font-bold text-yellow-800 flex items-center">
        <PlusCircle className="w-5 h-5 mr-2" />
        Admin: New Journal Entry
      </h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          placeholder="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="field"
          required
        />
        <input
          type="text"
          placeholder="Tags (comma separated, e.g., React, Coding)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          className="field"
        />
        <JournalEntryMetaFields
          docType={docType}
          setDocType={setDocType}
          changelog={changelog}
          setChangelog={setChangelog}
          accent="yellow"
        />
        <div className="space-y-2">
          <label className="block text-sm font-medium text-yellow-800">Image URLs (optional)</label>
          {imageUrls.map((url, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="url"
                placeholder={`Image URL ${index + 1}`}
                value={url}
                onChange={(e) => handleImageUrlChange(index, e.target.value)}
                className="field flex-1"
              />
              {imageUrls.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveImageField(index)}
                  className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddImageField}
            className="w-full py-2 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition"
          >
            + Add Another Image
          </button>
        </div>
        <JournalContentEditor
          ref={contentEditorRef}
          value={content}
          onChange={setContent}
          rows={14}
          accent="yellow"
        />
        <button
          type="submit"
          className="w-full py-2 bg-yellow-600 text-white font-semibold rounded-lg hover:bg-yellow-700 transition"
        >
          Publish Entry
        </button>
      </form>
    </div>
  );
};

export default AddPostForm;
